/**
 * Maison Grès — homepage / booking page form → cart → checkout.
 */
(function () {
  const Av = () => window.MaisonAvailability;

  function readUnitBlockedRules(form) {
    const rulesEl = form.querySelector('[data-maison-unit-blocked-rules]');
    if (!rulesEl) return {};
    try {
      return JSON.parse(rulesEl.textContent || '{}');
    } catch {
      return {};
    }
  }

  function setFormBusy(form, busy) {
    form.querySelectorAll('input, select, button').forEach((el) => {
      if (busy) {
        el.setAttribute('aria-disabled', 'true');
        if (el.tagName !== 'BUTTON') el.disabled = true;
      } else {
        el.removeAttribute('aria-disabled');
        if (el.tagName !== 'BUTTON') el.disabled = false;
      }
    });
  }

  function setSubmitLoading(btn, form, loading) {
    if (!btn) return;
    if (!btn.dataset.labelDefault) {
      btn.dataset.labelDefault = btn.textContent.trim();
    }
    const loadingLabel = form.getAttribute('data-loading-label') || 'Loading…';

    if (loading) {
      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      btn.textContent = loadingLabel;
      setFormBusy(form, true);
      btn.disabled = true;
    } else {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
      btn.textContent = btn.dataset.labelDefault;
      setFormBusy(form, false);
      updateBookingSubmitState(form, btn);
    }
  }

  function updateBookingSubmitState(form, submitBtn) {
    if (!submitBtn || submitBtn.classList.contains('is-loading')) return;

    const name = form.querySelector('[data-maison-booking-name]')?.value?.trim() || '';
    const phone = form.querySelector('[data-maison-booking-phone]')?.value?.trim() || '';
    const email = form.querySelector('[data-maison-booking-email]')?.value?.trim() || '';
    const guests = form.querySelector('[data-maison-booking-guests]')?.value || '';
    const unitSelect = form.querySelector('[data-maison-booking-unit]');
    const unit = unitSelect?.value || '';
    const variantId = unitSelect?.selectedOptions?.[0]?.getAttribute('data-variant-id') || '';
    const date = form.querySelector('[data-maison-booking-date]')?.value || '';
    const nights = Number(form.querySelector('[data-maison-booking-nights]')?.value || 0);

    const complete = Boolean(
      name &&
        phone &&
        email &&
        guests &&
        unit &&
        variantId &&
        date &&
        nights >= 1
    );

    submitBtn.disabled = !complete;
    submitBtn.setAttribute('aria-disabled', complete ? 'false' : 'true');
  }

  function initBookingForms(scope) {
    const root = scope || document;
    root.querySelectorAll('[data-maison-booking-checkout]').forEach((form) => {
      if (form.dataset.maisonBookingReady === 'true') return;
      form.dataset.maisonBookingReady = 'true';

      const errorEl = form.querySelector('[data-maison-booking-error]');
      const submitBtn =
        form.querySelector('[data-maison-booking-submit]') || form.querySelector('[type="submit"]');

      const syncSubmit = () => updateBookingSubmitState(form, submitBtn);
      form.addEventListener('input', syncSubmit);
      form.addEventListener('change', syncSubmit);
      syncSubmit();

      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        if (submitBtn?.classList.contains('is-loading') || submitBtn?.disabled) return;

        if (!form.checkValidity()) {
          form.reportValidity();
          syncSubmit();
          return;
        }

        const unitSelect = form.querySelector('[data-maison-booking-unit]');
        const option = unitSelect?.selectedOptions?.[0];
        const variantId = option?.getAttribute('data-variant-id');

        if (!variantId) {
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent =
              form.getAttribute('data-error-unit') || 'Please choose a residence.';
          }
          unitSelect?.focus();
          return;
        }

        const name = form.querySelector('[data-maison-booking-name]')?.value?.trim() || '';
        const phone = form.querySelector('[data-maison-booking-phone]')?.value?.trim() || '';
        const email = form.querySelector('[data-maison-booking-email]')?.value?.trim() || '';

        if (!name || !phone || !email) {
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent = 'Please fill in your name, phone number, and email.';
          }
          syncSubmit();
          return;
        }
        const guests = form.querySelector('[data-maison-booking-guests]')?.value || '';
        const unit = unitSelect?.value || '';
        const date = form.querySelector('[data-maison-booking-date]')?.value || '';
        const nightsInput = form.querySelector('[data-maison-booking-nights]');
        const nights = Math.max(
          1,
          Number(nightsInput?.value) ||
            Number(form.getAttribute('data-default-nights') || '1') ||
            1
        );

        const unitHandle = option?.getAttribute('data-product-handle') || '';
        const rulesMap = readUnitBlockedRules(form);
        const blockedSet = Av()?.expandRules(rulesMap[unitHandle] || '') || new Set();
        const checkIn = Av()?.parseYMD(date);
        const checkOut = checkIn ? Av()?.checkoutFromNights(checkIn, nights) : null;

        if (checkIn && checkOut && Av()?.stayHasBlockedNight(checkIn, checkOut, blockedSet)) {
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent =
              form.getAttribute('data-error-availability') ||
              'Those dates are not available for the selected residence. Please choose different dates.';
          }
          form.querySelector('[data-maison-booking-date]')?.focus();
          return;
        }

        const redirect = form.getAttribute('data-redirect') || '/checkout';

        if (errorEl) errorEl.hidden = true;
        setSubmitLoading(submitBtn, form, true);

        const properties = {
          'Check-in': date,
          'Check-out': checkOut ? Av().toYMD(checkOut) : '',
          Nights: String(nights),
          Guests: `${guests} guests`,
          Name: name,
          Phone: phone,
          Email: email,
          Unit: unit
        };

        try {
          await fetch('/cart/clear.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin'
          });

          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              items: [
                {
                  id: Number(variantId),
                  quantity: nights,
                  properties
                }
              ]
            })
          });

          if (!addRes.ok) {
            const err = await addRes.json().catch(() => ({}));
            throw new Error(err.description || err.message || 'Could not start checkout');
          }

          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              attributes: {
                'Booking name': name,
                'Booking phone': phone,
                'Booking email': email,
                'Booking guests': guests,
                'Booking unit': unit,
                'Booking date': date,
                'Booking nights': String(nights)
              }
            })
          });

          window.location.href = redirect;
        } catch (e) {
          console.error('Maison booking checkout failed', e);
          setSubmitLoading(submitBtn, form, false);
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent =
              e.message ||
              form.getAttribute('data-error-generic') ||
              'Something went wrong. Please try again.';
          }
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initBookingForms());
  } else {
    initBookingForms();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initBookingForms(event.target);
  });
})();
